import { Injectable, Inject, Injector } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { ToastrService } from 'ngx-toastr';
import { distinctUntilChanged, tap } from 'rxjs';

import { ScreenSizeService } from './screen-size.service';

@UntilDestroy()
@Injectable()
export class ToastNotificationService {
  private isHandset: boolean;

  constructor(
    @Inject(Injector) private injector: Injector,
    private dialog: MatDialog,
    private screenSizeService: ScreenSizeService
  ) {
    this.screenSizeService.isHandset$
      .pipe(
        untilDestroyed(this),
        distinctUntilChanged(),
        tap((isHandset) => {
          this.isHandset = isHandset;
        })
      )
      .subscribe();
  }

  // Need to get ToastrService from injector rather than constructor injection to avoid cyclic dependency error
  private get toastr(): ToastrService {
    return this.injector.get(ToastrService);
  }

  success(
    message: string,
    isSticky = false,
    title?: string,
    customClass?: string
  ): number {
    return this.showMessage(
      message,
      title,
      'toast-success',
      isSticky,
      customClass
    );
  }

  error(message: string, isSticky = false, title?: string): number {
    return this.showMessage(message, title, 'toast-error', isSticky);
  }

  warn(message: string, isSticky = false, title?: string): number {
    return this.showMessage(message, title, 'toast-warning', isSticky);
  }

  info(message: string, isSticky = false, title?: string): number {
    return this.showMessage(message, title, 'toast-info', isSticky);
  }

  hide(toastId: number): void {
    this.toastr.remove(toastId);
  }

  private showMessage(
    message: string,
    title?: string,
    type?: string,
    isSticky = false,
    customClass?: string
  ): number {
    return this.toastr.show(
      message,
      title,
      {
        positionClass: this.getToastPosition(),
        disableTimeOut: isSticky,
        messageClass: customClass,
      },
      type
    ).toastId;
  }

  private getToastPosition(): string {
    const hasFloating = this.hasFloatingButton();

    if (this.isHandset) {
      return hasFloating
        ? 'toast-inline-floating-button'
        : 'toast-bottom-center';
    }

    return hasFloating ? 'toast-above-floating-button' : 'toast-bottom-right';
  }

  private hasFloatingButton(): boolean {
    return (
      // Check if there are no open dialogs and a floating button exists
      !this.dialog.openDialogs.length &&
      !!document.querySelector('.floatingAddButton')
    );
  }
}
